<template>
  <div id="agLogPage">
    <!-- loading spinner, no workspaces, or no workflows -->
    <b-spinner v-if="isLoading" id="agLogPage-loadingSpinner"></b-spinner>
    <div id="agLogPage-noWss" v-else-if="workspaces.length == 0">
      <fa icon="briefcase" />
      <div>No workspaces have been created</div>
    </div>
    <div id="agLogPage-noLogs" v-else-if="logs.length == 0">
      <fa icon="clipboard-list" />
      <div>No logs to display</div>
    </div>

    <!-- delete modal -->
    <ag-logDelete-modal
      ref="deleteModal">
    </ag-logDelete-modal>

    <!-- filter modal -->
    <ag-logFilter-modal
      ref="filterModal"
      :filter="filter"
      @clearFilter="clearFilter"
      @applyFilter="applyFilter">
    </ag-logFilter-modal>

    <!-- toolbar -->
    <ag-toolbar>
      <template slot="leftContent">
        <b-button
          id="agLogPage-deleteButton"
          class="agImageButton"
          v-b-tooltip.hover
          title="Delete logs"
          :disabled="isLoading || workspaces.length == 0"
          @click="deleteButton_click">
          <fa icon="trash"/>
        </b-button>
        <b-button
          id="agLogPage-refreshButton"
          class="agImageButton"
          v-b-tooltip.hover
          title="Refresh logs"
          :disabled="isLoading || workspaces.length == 0"
          @click="refreshButton_click">
          <fa icon="sync-alt"/>
        </b-button>
        <div id="agLogPage-searchbar" class="agInputGroup">
          <b-button
            id="agLogPage-filterButton"
            class="agImageButton"
            :class="filterButtonClass"
            v-b-tooltip.hover
            title="Filter"
            :disabled="logs.length == 0"
            @click="filterButton_click">
            <fa icon="filter" />
          </b-button>
          <b-input v-model="searchText" type="text" autocomplete="false" placeholder="Search..."></b-input>
          <b-button 
            class="agTextButton agInputGroup-append" 
            @click="clearSearchButton_click">Clear</b-button>
        </div>
      </template>
      <template slot="rightContent">
        <div class="agInputGroup">
          <label>Workspace:</label>
          <b-form-select
            class="agLargeInput"
            v-model="workspace"
            :options="workspaceOptions"
            :disabled="isLoading || workspaces.length == 0"
          ></b-form-select>
        </div>
      </template>
    </ag-toolbar>

    <!-- logs -->
    <b-card id="agLogPage-logs" v-if="!isLoading && filteredLogs.length > 0">
      <b-table
        id="agLogPage-table"
        :key="'logs-' + reloadLogs"
        :items="filteredLogs"
        :fields="fields"
        bordered
        hover
        small>
        <template #cell(level)="data">
          <div v-if="data.value==-1">
            <fa class="agLogPage-levelSystem" icon="cog" />SYSTEM
          </div>
          <div v-else-if="data.value==1">
            <fa class="agLogPage-levelError" icon="exclamation-triangle" />ERROR
          </div>
        </template>
        <template #cell(message)="data">
          <span class="agLogPage-logMessage" v-html="getHighlightedMessage(data.value)"></span>
        </template>
      </b-table>
    </b-card>
  </div>
</template>





<script>
// components
import DeleteModal from "comp/log/DeleteModal";
import FilterModal from "comp/log/FilterModal";
import Toolbar from "ui/Toolbar";

// modules
import Server from "src/server";
import Util from "src/util/util";
import Moment from "moment";

export default {
  name: "ag-logs-page",
  components: {
    "ag-toolbar": Toolbar,
    "ag-logFilter-modal": FilterModal,
    "ag-logDelete-modal": DeleteModal
  },
  data() {
    return {
      reloadLogs: 0,
      filteredLogs: [],
      workspaceOptions: [],
      fields: [
        { key: "workflow.name", label: "Workflow", sortable: true },
        { key: "level", label: "Level", sortable: true },
        { key: "message", label: "Message", sortable: true },
        {
          key: "createdAt",
          label: "Date",
          sortable: true,
          formatter: "formatDate"
        }
      ],
      searchText: "",
      filter: {
        workflow: "",
        level: "",
        startDate: "",
        endDate: ""
      },
      filterApplied: false,
      webSocket: null
    };
  },
  computed: {
    isLoading() {
      if (
        this.$store.state.logStore.isLoading ||
        this.$store.state.wsStore.isLoading
      )
        return true;
      return false;
    },

    workspace: {
      get() {
        return this.$store.state.wsStore.workspace._id;
      },
      set(value) {
        this.$store.dispatch("wsStore/setWorkspace", {
          workspace: this.workspaces.find(a => a._id == value)
        });
      }
    },

    workspaces() {
      return this.$store.getters["wsStore/userWorkspaces"];
    },

    logs() {
      return this.$store.state.logStore.logs;
    },

    filterButtonClass() {
      if (this.filterApplied) return "agLogPage-filterButtonGreen";
      return "agLogPage-filterButtonGray";
    }
  },

  watch: {
    searchText() {
      this.setFilteredLogs();
    },

    workspaces() {
      this.setWorkspaceOptions();
    },

    logs() {
      this.setFilteredLogs();
    }
  },

  created() {
    // setup websocket
    this.webSocket = Server.initWebSocket();
    this.webSocket.onopen = () => {
      let message = {
        model: "log",
        workspace: this.workspace._id
      };
      message = JSON.stringify(message);
      this.webSocket.send(message);
    };
    this.webSocket.onmessage = message => {
      this.webSocket_onmessage(message);
    };
  },

  mounted() {
    this.setFilteredLogs();
    this.setWorkspaceOptions();
  },

  beforeDestroy() {
    this.webSocket.close();
  },

  methods: {
    // UI Events

    deleteButton_click() {
      this.$refs["deleteModal"].show();
    },

    refreshButton_click() {
      this.$store.dispatch("logStore/setLogs");
    },

    filterButton_click() {
      this.$refs.filterModal.show();
    },

    clearSearchButton_click() {
      this.searchText = "";
    },

    // Emitted Events

    clearFilter() {
      this.filterApplied = false;
      this.filter = {
        workflow: "",
        level: "",
        startDate: "",
        endDate: ""
      };
      this.setFilteredLogs();
      this.$refs.filterModal.hide();
    },

    applyFilter() {
      this.filterApplied = true;
      this.setFilteredLogs();
      this.$refs.filterModal.hide();
    },

    // other methods

    setFilteredLogs() {
      var logs = this.logs;


      // apply filters
      if (this.filter) {
        logs = logs.filter(log => {
          // workflow
          if (
            this.filter.workflow &&
            this.filter.workflow != log.workflow.name
          ) {
            return false;
          }

          // level
          if (this.filter.level && this.filter.level != log.level) {
            return false;
          }

          // start date
          if (this.filter.startDate) {
            var startDate = Moment(this.filter.startDate);
            var logDate = Moment(log.createdAt);
            if (startDate.isAfter(logDate)) {
              return false;
            }
          }

          //end date
          if (this.filter.endDate) {
            var endDate = Moment(this.filter.endDate);
            var logDate = Moment(log.createdAt);
            if (endDate.isBefore(logDate)) {
              return false;
            }
          }

          return true;
        });

        this.reloadLogs++;
      }

      // apply search text
      if (this.searchText) {
        logs = logs.filter(log => {
          return (
            log.message.toUpperCase().indexOf(this.searchText.toUpperCase()) >
            -1
          );
        });
      }

      this.filteredLogs = logs;
    },

    setWorkspaceOptions() {
      this.workspaceOptions = [];
      for (var i = 0; i < this.workspaces.length; i++) {
        var workspace = this.workspaces[i];
        this.workspaceOptions.push({
          value: workspace._id,
          text: workspace.name
        });
      }

      // select from userParms or first option if none selected
      if (this.workspaces.length > 0 && !this.workspace) {
        if (this.user.parms.workspace)
          var workspace = this.workspaces.find(
            a => a._id == this.user.parms.workspace
          );
        else {
          var workspace = this.workspaces[0];
        }
        this.$store.dispatch("wsStore/setWorkspace", { workspace: workspace });
        this.workspace = workspace;
      }
    },

    webSocket_onmessage(message) {
      var log = JSON.parse(message.data);
      this.$store.dispatch("logStore/addLog", { log: log });
    },

    formatDate(value) {
      var date = Moment(value).format("M/DD/YY h:mm:ss a");
      return date;
    },

    getHighlightedMessage(value) {
      if (!this.searchText) {
        return value;
      }

      return value.replace(new RegExp(this.searchText, "gi"), match => {
        return (
          "<span class='agLogPage-highlightedMessage'>" + match + "</span>"
        );
      });
    }
  }
};
</script>






<style scoped lang="css">
#agLogPage-logs {
  position: relative;
  height: calc(100vh - 150px);
  top: 65px;
  overflow: auto;
  text-align: left;
}

#agLogPage-loadingSpinner {
  position: absolute;
  width: 50px;
  height: 50px;
  left: 50%;
  top: 50%;
  margin-left: -20px;
  margin-top: -20px;
}

#agLogPage-noWss {
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -277px;
  margin-top: -61px;
  font-size: 36px;
  color: lightgray;
}

#agLogPage-noWss svg {
  font-size: 60px;
}

#agLogPage-noLogs {
  position: absolute;
  left: 50%;
  top: 50%;
  margin-left: -145px;
  margin-top: -61px;
  font-size: 36px;
  color: lightgray;
}

#agLogPage-noLogs svg {
  font-size: 60px;
}

#agLogPage-filterButton, #agLogPage-refreshButton, #agLogPage-deleteButton {
  font-size: 24px !important;
}

.agLogPage-filterButtonGreen {
  color: #4A797C;
}

.agLogPage-filterButtonGray,
.agLogPage-filterButtonGray:hover,
.agLogPage-filterButtonGray:disabled {
  color: lightgray !important;
}

#agLogPage-searchbar {
  display: inline-block;
}

#agLogPage-searchbar input {
  width: 400px;
}

#agLogPage-table {
  text-align: left;
}

.agLogPage-logMessage {
  white-space: pre-wrap;
  text-align: left !important;
}

.agLogPage-levelSystem {
  position: relative;
  color: #4A797C;
  font-size: 20px;
  margin-right: 5px;
  top: 2px;
}

.agLogPage-levelError {
  color: #C82333;
  font-size: 18px;
  margin-right: 5px;
}

#agLogPage-filterModal .agInputGroup {
  margin: 25px 0px;
}

#agLogPage-filterModal .agInputGroup label {
  width: 100px;
}
</style>


<style lang="css">
.agLogPage-highlightedMessage {
  color: #C82333;
}

#agLogPage-table td:nth-child(1) {
  width: 200px;
}

#agLogPage-table td:nth-child(2) {
  width: 115px;
}

#agLogPage-table td:nth-child(4) {
  width: 115px;
}
</style>


// WEBPACK FOOTER //
// src/components/log/LogsPage.vue